import { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { api } from '../lib/api';
import type { RoadmapResponse, BurndownPoint } from '@sprintpulse/shared';

Chart.register(...registerables);

interface Props {
  roadmapData: RoadmapResponse;
  productName: string;
  quarterLabel: string;
  onClose: () => void;
}

type Step = 'configure' | 'loading' | 'review' | 'publishing' | 'success' | 'error';

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14,2 14,8 20,8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const ExternalIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function Spinner({ color = '#1D4ED8' }: { color?: string }) {
  return (
    <div className="w-7 h-7 rounded-full border-2 animate-spin"
      style={{ borderColor: color, borderTopColor: 'transparent' }} />
  );
}

function StepIndicator({ current }: { current: number }) {
  const steps = ['Configure', 'Review', 'Publish'];
  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: done ? 'var(--green)' : active ? '#1D4ED8' : 'var(--border)',
                color: done || active ? '#fff' : 'var(--text-3)',
              }}
            >
              {done ? '✓' : idx}
            </div>
            <span className="text-xs" style={{ color: active ? 'var(--text-1)' : 'var(--text-3)', fontWeight: active ? 600 : 400 }}>
              {label}
            </span>
            {i < steps.length - 1 && (
              <div className="w-6 h-px mx-1" style={{ background: 'var(--border)' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Render burndown chart to an offscreen canvas and return base64 PNG (no data: prefix). */
function renderBurndownPng(points: BurndownPoint[], title: string): string | null {
  if (points.length < 2) return null;
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 400;

  const lastActualIdx = points.findIndex((p) => p.forecast) - 1;
  const hasForecast = points.some((p) => p.forecast);

  const actualRemaining = points.map((p, i) =>
    !p.forecast || (hasForecast && i === lastActualIdx + 1) ? p.remaining : null,
  );
  const actualDone = points.map((p) => (!p.forecast ? p.done : null));
  const forecastRemaining = points.map((p, i) =>
    p.forecast || i === lastActualIdx ? p.remaining : null,
  );
  const forecastDone = points.map((p, i) =>
    p.forecast || i === lastActualIdx ? p.done : null,
  );

  const lastForecast = points[points.length - 1];
  const willComplete = lastForecast && lastForecast.remaining === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const datasets: any[] = [
    { label: 'Remaining', data: actualRemaining, borderColor: '#6366f1', backgroundColor: '#6366f120', fill: true, tension: 0.3, pointRadius: 2, spanGaps: true },
    { label: 'Done', data: actualDone, borderColor: '#22c55e', backgroundColor: '#22c55e20', fill: true, tension: 0.3, pointRadius: 2, spanGaps: true },
  ];

  if (hasForecast) {
    const fc = willComplete ? '#22c55e' : '#ef4444';
    datasets.push(
      { label: 'Forecast (remaining)', data: forecastRemaining, borderColor: fc, borderDash: [6, 3], backgroundColor: 'transparent', fill: false, tension: 0.3, pointRadius: 2, pointStyle: 'triangle' as const, spanGaps: true },
      { label: 'Forecast (done)', data: forecastDone, borderColor: '#22c55e', borderDash: [6, 3], backgroundColor: 'transparent', fill: false, tension: 0.3, pointRadius: 2, pointStyle: 'triangle' as const, spanGaps: true },
    );
  }

  const chart = new Chart(canvas, {
    type: 'line',
    data: { labels: points.map((p) => p.date), datasets },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
        title: { display: true, text: title, font: { size: 14, weight: 'normal' } },
      },
      scales: {
        x: { ticks: { font: { size: 10 }, maxRotation: 45 } },
        y: { beginAtZero: true, ticks: { font: { size: 10 } } },
      },
    },
  });

  const dataUrl = canvas.toDataURL('image/png');
  chart.destroy();
  // Strip "data:image/png;base64," prefix
  return dataUrl.replace(/^data:image\/png;base64,/, '');
}

export function RoadmapConfluenceReportModal({ roadmapData, productName, quarterLabel, onClose }: Props) {
  const defaultTitle = `${productName} - ${quarterLabel} Roadmap Report`;
  const [step, setStep] = useState<Step>('configure');
  const [parentPageUrl, setParentPageUrl] = useState('');
  const [pageTitle, setPageTitle] = useState(defaultTitle);
  const [narrative, setNarrative] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState<{ pageUrl: string; pageTitle: string } | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const stepNumber = step === 'configure' || step === 'loading' ? 1
    : step === 'review' ? 2
    : 3;

  useEffect(() => {
    if (step === 'configure') urlInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  // Compute summary stats
  const totalEpics = roadmapData.epics.length;
  const totalItems = roadmapData.epics.reduce((s, e) => s + e.totalIssues, 0);
  const doneItems = roadmapData.epics.reduce((s, e) => s + e.doneIssues, 0);
  const blockedItems = roadmapData.epics.reduce((s, e) => s + e.blockedIssues, 0);
  const overallPercent = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  async function handleLoadPreview() {
    if (!parentPageUrl.trim()) return;
    setStep('loading');
    setErrorMsg('');
    try {
      const res = await api.getRoadmapConfluencePreview({
        productName,
        quarterLabel,
        epics: roadmapData.epics,
        burndown: roadmapData.burndown,
        productGroups: roadmapData.productGroups,
        themes: roadmapData.themes,
      });
      setNarrative(res.narrative);
      setStep('review');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Failed to generate narrative');
      setStep('error');
    }
  }

  async function handlePublish() {
    setStep('publishing');
    try {
      // Render burndown chart to PNG for Confluence attachment
      const burndownImage = renderBurndownPng(
        roadmapData.burndown,
        `${quarterLabel} Burndown — ${productName}`,
      ) ?? undefined;

      const res = await api.publishRoadmapConfluenceReport({
        parentPageUrl: parentPageUrl.trim(),
        pageTitle: pageTitle.trim() || defaultTitle,
        narrative,
        productName,
        quarterLabel,
        epics: roadmapData.epics,
        burndown: roadmapData.burndown,
        productGroups: roadmapData.productGroups,
        themes: roadmapData.themes,
        burndownImage,
      });
      setResult({ pageUrl: res.pageUrl, pageTitle: res.pageTitle });
      setStep('success');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Failed to publish report');
      setStep('error');
    }
  }

  const inputStyle = {
    background: 'var(--bg-input, var(--bg))',
    borderColor: 'var(--border)',
    color: 'var(--text-1)',
  };

  const cancelBtn = (
    <button onClick={onClose} className="text-xs px-4 py-2 rounded-md border"
      style={{ color: 'var(--text-2)', borderColor: 'var(--border)', background: 'var(--bg-panel)' }}>
      Cancel
    </button>
  );

  const blueBtn = (label: string, onClick: () => void, disabled = false) => (
    <button onClick={onClick} disabled={disabled}
      className="text-xs px-4 py-2 rounded-md font-medium"
      style={{ background: disabled ? 'var(--border)' : '#1D4ED8', color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-xl border shadow-xl flex flex-col"
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)', maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: '#1D4ED8', color: '#fff' }}>
              <DocIcon />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>Generate Roadmap Report</p>
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>{productName} - {quarterLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded flex items-center justify-center text-lg leading-none"
            style={{ color: 'var(--text-3)', background: 'var(--bg-subtle)' }}>x</button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={stepNumber} />

        {/* Scrollable body */}
        <div className="px-5 py-5 overflow-y-auto flex-1">

          {/* STEP 1: Configure */}
          {(step === 'configure' || step === 'error') && (
            <div className="space-y-4">
              {step === 'error' && (
                <div className="text-xs px-3 py-2.5 rounded-md border"
                  style={{ background: 'var(--red-dim)', borderColor: 'var(--red)', color: 'var(--red)' }}>
                  {errorMsg}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>
                  Confluence Parent Page URL <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input ref={urlInputRef} type="url" value={parentPageUrl}
                  onChange={(e) => setParentPageUrl(e.target.value)}
                  placeholder="https://company.atlassian.net/wiki/spaces/ENG/pages/123456/Roadmap+Reports"
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none"
                  style={inputStyle}
                  onKeyDown={(e) => { if (e.key === 'Enter' && parentPageUrl.trim()) handleLoadPreview(); }} />
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                  Paste the URL of the page you want the report nested under
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>Page Title</label>
                <input type="text" value={pageTitle} onChange={(e) => setPageTitle(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none" style={inputStyle} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                {cancelBtn}
                {blueBtn('Next - Generate Preview \u2192', handleLoadPreview, !parentPageUrl.trim())}
              </div>
            </div>
          )}

          {/* LOADING */}
          {step === 'loading' && (
            <div className="flex flex-col items-center py-10 gap-4">
              <Spinner />
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Generating report...</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Creating AI narrative from roadmap data</p>
              </div>
            </div>
          )}

          {/* STEP 2: Review */}
          {step === 'review' && (
            <div className="space-y-5">
              {/* Summary */}
              <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-3)' }}>Roadmap Summary</p>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Epics', value: totalEpics, color: 'var(--accent)' },
                    { label: 'Done', value: `${doneItems} (${overallPercent}%)`, color: 'var(--green)' },
                    { label: 'Items', value: totalItems, color: 'var(--text-1)' },
                    { label: 'Blocked', value: blockedItems, color: blockedItems > 0 ? 'var(--red)' : 'var(--text-3)' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-md p-3 text-center" style={{ background: 'var(--bg-subtle)' }}>
                      <p className="text-xs" style={{ color: 'var(--text-3)' }}>{label}</p>
                      <p className="text-lg font-bold" style={{ color }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product chips */}
              <div className="flex flex-wrap gap-2">
                {roadmapData.productGroups.map((cat) => {
                  const catEpics = roadmapData.epics.filter((e) => e.productGroup === cat);
                  const catTotal = catEpics.reduce((s, e) => s + e.totalIssues, 0);
                  const catDone = catEpics.reduce((s, e) => s + e.doneIssues, 0);
                  const catPct = catTotal > 0 ? Math.round((catDone / catTotal) * 100) : 0;
                  return (
                    <span key={cat} className="text-xs px-2.5 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }}>
                      <span className="font-medium" style={{ color: 'var(--text-1)' }}>{cat}</span>
                      <span style={{ color: 'var(--text-3)' }}> {catPct}%</span>
                    </span>
                  );
                })}
              </div>

              {/* AI Narrative */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <label className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>Comments / Narrative</label>
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '10px' }}>
                    AI draft - edit before publishing
                  </span>
                </div>
                <textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  rows={10}
                  className="w-full text-xs px-3 py-2.5 rounded-md border outline-none resize-none font-mono leading-relaxed"
                  style={{ background: 'var(--bg-input, var(--bg))', borderColor: 'var(--border)', color: 'var(--text-1)' }}
                />
              </div>

              <div className="flex justify-between items-center pt-1">
                <button onClick={() => setStep('configure')} className="text-xs" style={{ color: 'var(--text-3)' }}>
                  ← Back
                </button>
                <div className="flex gap-2">
                  {cancelBtn}
                  {blueBtn('Publish to Confluence \u2192', handlePublish)}
                </div>
              </div>
            </div>
          )}

          {/* PUBLISHING */}
          {step === 'publishing' && (
            <div className="flex flex-col items-center py-10 gap-4">
              <Spinner />
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Publishing to Confluence...</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Creating page under your chosen parent</p>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {step === 'success' && result && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 px-4 py-3 rounded-md border"
                style={{ background: 'var(--green-dim)', borderColor: 'var(--green)' }}>
                <span style={{ color: 'var(--green)', fontSize: '20px', lineHeight: 1 }}>✓</span>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Page published successfully</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{result.pageTitle}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="text-xs px-4 py-2 rounded-md border"
                  style={{ color: 'var(--text-2)', borderColor: 'var(--border)', background: 'var(--bg-panel)' }}>
                  Close
                </button>
                <a href={result.pageUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs px-4 py-2 rounded-md font-medium flex items-center gap-1.5"
                  style={{ background: '#1D4ED8', color: '#fff', textDecoration: 'none' }}>
                  Open in Confluence <ExternalIcon />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
